import type {
  OperatoryId,
  OperatoryPolicyPayload,
  OperatoryPolicyResponse,
} from './operatory-policy-contracts'

export type Operatory = {
  id: OperatoryId
  name: string
}

export type OperatoryPolicy = {
  operatories: Operatory[]
  blockedOperatories: OperatoryId[]
  trueOverlappingOperatorySlots: boolean
}

function idKey(id: OperatoryId) {
  return `${typeof id}:${String(id)}`
}

export function normalizeOperatoryPolicy(
  response: OperatoryPolicyResponse
): OperatoryPolicy | null {
  const operatories = Object.values(response.operatories ?? {}).sort(
    (left, right) => left.name.localeCompare(right.name)
  )
  const seen = new Set<string>()
  for (const operatory of operatories) {
    const key = idKey(operatory.id)
    if (seen.has(key)) return null
    seen.add(key)
  }

  return {
    operatories,
    blockedOperatories: response.blockedOperatories,
    trueOverlappingOperatorySlots: response.trueOverlappingOperatorySlots,
  }
}

export function isOperatoryBlocked(
  blockedOperatories: OperatoryId[],
  operatoryId: OperatoryId
) {
  const key = idKey(operatoryId)
  return blockedOperatories.some((id) => idKey(id) === key)
}

export function buildOperatoryPolicyPayload(
  policy: OperatoryPolicy,
  blockedKnownIds: ReadonlySet<string>,
  trueOverlappingOperatorySlots: boolean
): OperatoryPolicyPayload {
  const knownIds = new Set(policy.operatories.map((item) => idKey(item.id)))
  const unmatchedIds = policy.blockedOperatories.filter(
    (id) => !knownIds.has(idKey(id))
  )
  const selectedKnownIds = policy.operatories
    .filter((item) => blockedKnownIds.has(idKey(item.id)))
    .map((item) => item.id)

  return {
    blockedOperatories: [...unmatchedIds, ...selectedKnownIds],
    trueOverlappingOperatorySlots,
  }
}

export function blockedKnownIdKeys(policy: OperatoryPolicy) {
  return new Set(
    policy.operatories
      .filter((item) => isOperatoryBlocked(policy.blockedOperatories, item.id))
      .map((item) => idKey(item.id))
  )
}

export function operatoryIdKey(id: OperatoryId) {
  return idKey(id)
}
